"use client"

import { useCallback } from "react"
import { toast } from "sonner"
import { FeatureChecks } from "@/lib/feature-flags"
import { useTaskStore } from "@/stores"
import { usePersistentTimerStore } from "@/stores/persistent-timer-store"
import { usePomodoroTimerStore } from "@/stores/pomodoro-timer-store"
import { useCommandPaletteStore } from "@/stores/command-palette-store"
import { useComponentsTranslations, useTimerTranslations } from "@/hooks/use-translations"
import type { Task } from "@/stores/task-store"
import type { TaskShortcutAction } from "@/stores/command-palette-store"

export function useCommandShortcutActions() {
  const tComp = useComponentsTranslations()
  const tTimer = useTimerTranslations()
  const updateTask = useTaskStore((state) => state.updateTask)
  const openTaskActionPicker = useCommandPaletteStore((state) => state.openTaskActionPicker)
  const currentTaskTimer = usePersistentTimerStore((state) => state.currentTimer)
  const pomodoroTaskId = usePomodoroTimerStore((state) => state.selectedTaskId)
  const pomodoroIsActive = usePomodoroTimerStore((state) => state.isActive)

  const hasTaskTimer = Boolean(currentTaskTimer)
  const hasPomodoro = Boolean(pomodoroTaskId || pomodoroIsActive)

  const openTaskActionShortcut = useCallback((action: TaskShortcutAction) => {
    const startingTimer = action === "start-task-timer" || action === "start-pomodoro-timer"
    if (startingTimer && (hasTaskTimer || hasPomodoro)) {
      toast.error(tComp("command_palette_end_current_timer_first"))
      return false
    }

    openTaskActionPicker(action)
    return true
  }, [hasPomodoro, hasTaskTimer, openTaskActionPicker, tComp])

  const pauseTaskTimer = useCallback(async () => {
    const timerStore = usePersistentTimerStore.getState()

    if (timerStore.currentTimer?.status !== "running") {
      toast.error(tComp("command_palette_task_timer_not_running"))
      return false
    }

    const paused = await timerStore.pauseTimer()
    if (paused) {
      toast.success(tComp("command_palette_task_timer_paused"))
    }
    return paused
  }, [tComp])

  const endTaskTimer = useCallback(async () => {
    const timerStore = usePersistentTimerStore.getState()

    if (!timerStore.currentTimer) {
      toast.error(tComp("command_palette_task_timer_not_active"))
      return false
    }

    const stopped = await timerStore.stopTimer()
    if (stopped) {
      toast.success(tComp("command_palette_task_timer_ended"))
    }
    return stopped
  }, [tComp])

  const startTaskTimerForTask = useCallback(async (task: Task) => {
    const timerUsage = await FeatureChecks["useTimer"]()
    if (!timerUsage.allowed) {
      toast.error(
        tTimer("timer_limit_reached_upgrade", {
          limit: timerUsage.limit,
          plural: timerUsage.limit === 1 ? "" : "s",
          plan: timerUsage.user_role,
        }),
      )
      return false
    }

    const started = await usePersistentTimerStore.getState().startTimer(task.id)
    if (started) {
      toast.success(tComp("command_palette_task_timer_started", { taskTitle: task.title }))
    }
    return started
  }, [tComp, tTimer])

  const pausePomodoroTimer = useCallback(async () => {
    const pomodoroStore = usePomodoroTimerStore.getState()

    if (!pomodoroStore.isRunning) {
      toast.error(tComp("command_palette_pomodoro_not_running"))
      return false
    }

    pomodoroStore.startPause()
    toast.success(tComp("command_palette_pomodoro_paused"))
    return true
  }, [tComp])

  const endPomodoroTimer = useCallback(async () => {
    const pomodoroStore = usePomodoroTimerStore.getState()

    if (!pomodoroStore.isActive && !pomodoroStore.selectedTaskId) {
      toast.error(tComp("command_palette_pomodoro_not_active"))
      return false
    }

    pomodoroStore.reset()
    pomodoroStore.clearTask()
    toast.success(tComp("command_palette_pomodoro_ended"))
    return true
  }, [tComp])

  const startPomodoroForTask = useCallback(async (task: Task) => {
    const pomodoroStore = usePomodoroTimerStore.getState()

    if (pomodoroStore.showPhaseComplete) {
      pomodoroStore.advancePhase()
    }

    if (!pomodoroStore.selectedTaskId || pomodoroStore.selectedTaskId !== task.id) {
      pomodoroStore.setSelectedTaskId(task.id)
    }

    if (!pomodoroStore.isRunning) {
      pomodoroStore.startPause()
    }

    toast.success(tComp("command_palette_pomodoro_started", { taskTitle: task.title }))
    return true
  }, [tComp])

  const markTaskComplete = useCallback(async (task: Task) => {
    await updateTask(task.id, { completed: true })

    const timerStore = usePersistentTimerStore.getState()
    if (timerStore.currentTimer?.task_id === task.id) {
      await timerStore.stopTimer()
    }

    const pomodoroStore = usePomodoroTimerStore.getState()
    if (pomodoroStore.selectedTaskId === task.id) {
      pomodoroStore.reset()
      pomodoroStore.clearTask()
    }

    toast.success(tComp("command_palette_task_completed", { taskTitle: task.title }))
    return true
  }, [tComp, updateTask])

  return {
    hasTaskTimer,
    hasPomodoro,
    openTaskActionShortcut,
    startTaskTimerForTask,
    pauseTaskTimer,
    endTaskTimer,
    startPomodoroForTask,
    pausePomodoroTimer,
    endPomodoroTimer,
    markTaskComplete,
  }
}
